import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Dynamic gradient for match score badge using the status palette
function hexToRgb(hex: string): [number, number, number] {
  hex = hex.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(x => x + x).join('');
  const num = parseInt(hex, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpColor(colorA: string, colorB: string, t: number): string {
  const rgbA = hexToRgb(colorA);
  const rgbB = hexToRgb(colorB);
  return rgbToHex([
    Math.round(lerp(rgbA[0], rgbB[0], t)),
    Math.round(lerp(rgbA[1], rgbB[1], t)),
    Math.round(lerp(rgbA[2], rgbB[2], t)),
  ]);
}

// Score-gradient anchor colors. Names kept for call-site stability; values
// track the redesign status palette (--success / --warning / --destructive).
const GONK_GREEN = '#16a34a'; // success
const GONK_YELLOW = '#d97706'; // warning
const GONK_RED = '#dc2626'; // destructive

/**
 * Anchor points for the nonlinear match score gradient.
 * Adjust these to change the bottom, mid, and top thresholds.
 */
export const GONK_SCORE_BOTTOM = 50;
export const GONK_SCORE_MID = 80;
export const GONK_SCORE_TOP = 95;

/**
 * Returns a background/text color style for a score (0-100) using a nonlinear status-palette gradient.
 * - <=BOTTOM: solid red
 * - BOTTOM–MID: red to yellow (ease-in)
 * - MID–TOP: yellow to green (ease-out)
 * - >=TOP: solid green
 * @param score number from 0-100
 */
export function getGonkScoreStyle(score: number): { background: string; color: string } {
  // Anchor points
  const RED = GONK_RED;
  const YELLOW = GONK_YELLOW;
  const GREEN = GONK_GREEN;
  const BOTTOM = GONK_SCORE_BOTTOM;
  const MID = GONK_SCORE_MID;
  const TOP = GONK_SCORE_TOP;
  let bg: string;
  if (score <= BOTTOM) {
    bg = RED;
  } else if (score <= MID) {
    // Ease-in for red to yellow
    const t = Math.pow((score - BOTTOM) / (MID - BOTTOM), 1.5); // nonlinear, 0 at BOTTOM, 1 at MID
    bg = lerpColor(RED, YELLOW, t);
  } else if (score <= TOP) {
    // Ease-out for yellow to green
    const t = 1 - Math.pow((TOP - score) / (TOP - MID), 1.5); // nonlinear, 0 at MID, 1 at TOP
    bg = lerpColor(YELLOW, GREEN, t);
  } else {
    bg = GREEN;
  }
  // Use dark text for light backgrounds, white for dark
  const [r, g, b] = hexToRgb(bg);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const color = luminance > 0.8 ? '#43300A' : '#fff';
  return { background: bg, color };
}

/**
 * Utility functions for the Overmatch interface.
 *
 * Includes helpers for badge color mapping, classnames, and more.
 */

/**
 * Returns a color string for a given badge name.
 * Used to map badge text to a consistent color for UI display.
 *
 * @param {string} badgeName - The name/text of the badge
 * @returns {string} - The color associated with the badge
 *
 * Example:
 *   getBadgeColor('Cyber') // 'blue'
 *   getBadgeColor('Unknown') // 'gray'
 */
/**
 * Human-readable display names for agent tool calls.
 * Maps internal tool names to user-friendly labels.
 */
const TOOL_DISPLAY_NAMES: Record<string, string> = {
  add_scorecard_item: 'Added Scorecard Item',
  remove_scorecard_item: 'Removed Scorecard Item',
  update_scorecard_item: 'Updated Scorecard Item',
  search_specs: 'Searched Specifications',
  list_program_scorecards: 'Listed Scorecards',
  search_market: 'Searched Market',
  get_vendor_detail: 'Fetched Vendor Details',
  get_program: 'Fetched Program',
  get_program_summary: 'Fetched Program Summary',
  get_scorecard: 'Fetched Scorecard',
  suggest_navigation: 'Suggested Navigation',
  // Exec agent only (exec_setup script); the program agent no longer has it.
  advance_workflow_step: 'Advanced Workflow Step',
  list_workflows: 'Listed Workflows',
  start_workflow: 'Started Workflow',
  get_program_workflow: 'Fetched Program Workflow',
  list_program_vendors: 'Listed Vendors',
  list_program_documents: 'Listed Documents',
  get_document: 'Fetched Document',
  edit_document: 'Edited Document',
  get_evaluation_status: 'Checked Evaluation Status',
  research_technology_market: 'Suggested Market Research',
  list_technologies: 'Listed Technologies',
};

/**
 * Returns a human-readable display name for an agent tool call.
 * Falls back to a title-cased version of the tool name if not found in the map.
 */
export function getToolDisplayName(toolName: string): string {
  return TOOL_DISPLAY_NAMES[toolName] || toolName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function getBadgeColor(badgeName: string): string {
  const colorMap: Record<string, string> = {
    Cyber: 'blue',
    'Adversary Sims': 'red',
    'Space Ops': 'purple',
    'Threat Intel': 'yellow',
    'Fixed Price': 'green',
    Logistics: 'blue',
    // Add more mappings as needed
  };
  return colorMap[badgeName] || 'gray';
}
